import { Controller } from '@hotwired/stimulus';
import { initMask } from '../../packs/mask';
import { selectselectize } from '../../packs/custom_selectize';

// Connects to data-controller="forms--route-section"
export default class extends Controller {
  static get targets() {
    return [
      'totalRefValue',
      'totalTollPrice',
      'totalTransitTime',
      'totalKilometers',
      'roadIconTemplate',
      'railwayIconTemplate',
      'roundStepTemplate',
      'roundTransitionFieldsTemplate',
      'addRoundStepButton',
      'modalTransport',
      'transitionModalIndicator',
    ];
  }

  static get values() {
    return { calculateTransitTimeUrl: String };
  }

  connect() {
    this.calculateTotalKilometers();
    this.calculateTotalTollPrice();
    this.calculateTotalRefValue();
    this.syncModalTransportIndicators();
    this.updateAllModalResumes();
    initMask();
  }

  get modalTransportInput() {
    if (!this.hasModalTransportTarget) return null;

    return this.modalTransportTarget.querySelector(
      '[data-components--segmented-toggle-target="input"]'
    );
  }

  get modalTransportLabel() {
    return this.modalTransportInput?.dataset.label ?? '';
  }

  syncModalTransportIndicators() {
    const isRailway = this.modalTransportLabel === 'Ferroviário';

    this.transitionModalIndicatorTargets.forEach((indicator) => {
      const icon = indicator.querySelector('.transitionModalIndicatorIcon');
      const label = indicator.querySelector('.transitionModalIndicatorLabel');

      icon?.classList.toggle('fa-train', isRailway);
      icon?.classList.toggle('fa-truck', !isRailway);

      if (label) label.textContent = this.modalTransportLabel;
    });
  }

  handleModalTransportChange() {
    this.syncModalTransportIndicators();
    this.syncTransitionModalTransportId();
    this.updateAllModalResumes();
  }

  syncTransitionModalTransportId() {
    const modalTransportId = this.modalTransportInput?.value;
    if (!modalTransportId) return;

    this.element
      .querySelectorAll('.transitionFields [name$="[modal_transport_id]"]')
      .forEach((input) => {
        input.value = modalTransportId;
      });
  }

  fetchTargetIndex(event) {
    const target = event.currentTarget;
    const wrapper = target.closest('[data-index]');
    const index = wrapper?.dataset?.index;
    return { target, index };
  }

  addRoundStep() {
    const tfTemplate = this.roundTransitionFieldsTemplateTarget;
    tfTemplate.replaceWith(tfTemplate.content.cloneNode(true));

    const stepTemplate = this.roundStepTemplateTarget;
    stepTemplate.before(stepTemplate.content.cloneNode(true));
    stepTemplate.remove();

    this.addRoundStepButtonTarget.remove();
    selectselectize();
    initMask();
  }

  handlePlaceStrategy(event) {
    const { target: button, index } = this.fetchTargetIndex(event);
    const { label } = button.dataset;
    const allowedForPlaces = ['TERMINAL INTERMODAL', 'PORTO'];
    const placeWrapper = document.querySelector(
      `[data-index="${index}"] .placeWrapper`
    );
    const cityStateWrapper = document.querySelector(
      `[data-index="${index}"] .cityStateWrapper`
    );

    this.updateResume(index, '#routeSectionStepPlace', '');
    if (allowedForPlaces.includes(label.toUpperCase())) {
      this.loadPlaces(button.dataset.value, index);
      this.toggleWrapper(placeWrapper, true);
      this.toggleWrapper(cityStateWrapper, false);
    } else {
      this.toggleWrapper(placeWrapper, false);
      this.toggleWrapper(cityStateWrapper, true);
    }

    const stepLabel = { 0: 'Origem', 1: 'Destino', 2: 'Parada' }[index];
    const placeTypeLabel = button.dataset.label;

    this.updateResume(
      index,
      '#routeSectionStepName',
      `${stepLabel} - ${placeTypeLabel}`
    );
  }

  toggleWrapper(wrapper, enabled) {
    wrapper?.classList.toggle('d-none', !enabled);

    wrapper?.querySelectorAll('input, select, textarea').forEach((field) => {
      field.disabled = !enabled;
    });
  }

  async loadPlaces(placeTypeId, index) {
    const select = document.querySelector(
      `.placeWrapper select.selectize[data-index="${index}"]`
    );

    const { selectize } = select;

    selectize.clear();
    selectize.clearOptions();

    const response = await fetch(
      `/clients/client_by_type?place_type_id=${placeTypeId}`
    );
    const clients = await response.json();
    selectize.addOption(clients);
  }

  placeChange(event) {
    const { target: select, index } = this.fetchTargetIndex(event);

    const placeName = select.options[select.selectedIndex]?.text || '';
    this.updateResume(index, '#routeSectionStepPlace', placeName);
  }

  updateModalResumeFromEvent(event) {
    const { index } = this.fetchTargetIndex(event);
    this.updateModalResume(index);
  }

  updateModalResume(stepIndex) {
    const index = parseInt(stepIndex, 10);
    const resumeIndex = index - 1;
    if (resumeIndex < 0) return;

    const transition = this.element.querySelector(
      `.transitionFields[data-index="${index}"]`
    );

    if (!transition) return;

    const modalLabel = this.modalTransportLabel;
    if (!modalLabel) return;

    const tripInput = transition.querySelector('[name$="[empty_trip]"]');
    const emptyTrip = tripInput?.dataset?.label;

    const template =
      modalLabel === 'Rodoviário'
        ? this.roadIconTemplateTarget
        : this.railwayIconTemplateTarget;

    const icon = template.content.firstElementChild.cloneNode(true);
    const fragment = document.createDocumentFragment();

    fragment.appendChild(icon);
    fragment.appendChild(
      document.createTextNode(` ${modalLabel} - ${emptyTrip}`)
    );

    this.updateResume(resumeIndex, '#routeSectionStepIcon', fragment);
  }

  updateAllModalResumes() {
    this.element
      .querySelectorAll('.resumeWrapper #routeSectionStepIcon')
      .forEach((iconEl) => iconEl.replaceChildren());

    this.element.querySelectorAll('.transitionFields').forEach((transition) => {
      this.updateModalResume(transition.dataset.index);
    });
  }

  async stepTransitTime(event) {
    const { target: input } = this.fetchTargetIndex(event);
    const kilometers = parseInt(input.value, 10) || 0;
    const transitTimeInput = input
      .closest(`.transitionFields`)
      .querySelector('.transitTime');
    const transitTime = await this.fetchTransitTime(kilometers);
    transitTimeInput.value = transitTime ?? '';
    transitTimeInput.dispatchEvent(new Event('change'));

    this.calculateTotalKilometers();
  }

  updateResume(index, target, content) {
    const resumeWrapper = document.querySelector(
      `.resumeWrapper[data-index="${index}"]`
    );

    if (!resumeWrapper) return;

    const element = resumeWrapper.querySelector(target);

    if (!element) return;

    if (content instanceof Node) {
      element.replaceChildren(content);
    } else {
      element.textContent = content;
    }
  }

  calculateTotalKilometers() {
    const inputs = this.element.querySelectorAll('input[data-kilometers]');

    const total = Array.from(inputs).reduce((sum, input) => {
      return sum + (parseInt(input.value, 10) || 0);
    }, 0);

    this.totalKilometersTarget.textContent = total;
    this.calculateTotalTransitTime(total);
  }

  calculateTotalRefValue() {
    const inputs = this.element.querySelectorAll(
      'input[type="hidden"][id$="ref_value"]'
    );

    const total = Array.from(inputs).reduce((sum, input) => {
      return sum + (parseFloat(input.value) || 0);
    }, 0);

    this.totalRefValueTarget.textContent = total.toFixed(2).replace('.', ',');
  }

  calculateTotalTollPrice() {
    const inputs = this.element.querySelectorAll(
      'input[type="hidden"][id$="toll_price"]'
    );

    const total = Array.from(inputs).reduce((sum, input) => {
      return sum + (parseFloat(input.value) || 0);
    }, 0);

    this.totalTollPriceTarget.textContent = total.toFixed(2).replace('.', ',');
  }

  async calculateTotalTransitTime(totalKilometers) {
    const transitTime = await this.fetchTransitTime(totalKilometers);
    this.totalTransitTimeTarget.textContent = transitTime ?? '';
  }

  async fetchTransitTime(kilometers) {
    const response = await fetch(
      `${this.calculateTransitTimeUrlValue}?kilometers=${kilometers}`
    );

    const data = await response.json();
    return data.transit_time ?? null;
  }

  cityChange(event) {
    const { target: select, index } = this.fetchTargetIndex(event);
    const wrapper = select.closest('.cityStateWrapper');
    const stateSelect = wrapper.querySelector('select[name$="[state]"]');
    const stateName = stateSelect.options[stateSelect.selectedIndex]?.text;
    const cityName = select.options[select.selectedIndex]?.text;

    this.updateResume(
      index,
      '#routeSectionStepPlace',
      `${cityName}/${stateName}`
    );
  }
}
